'use client';
import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { useI18n } from '@/i18n';

// Animated number counter
function AnimatedNumber({ value, suffix = '' }: { value: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  return (
    <span ref={ref} className="tabular-nums">
      {isInView ? (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {value}
          {suffix}
        </motion.span>
      ) : (
        <span>0{suffix}</span>
      )}
    </span>
  );
}

// Single metric row
function MetricRow({
  value,
  label,
  description,
  index,
}: {
  value: string;
  label: string;
  description: string;
  index: number;
}) {
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''));
  const suffix = value.includes('%') ? '%' : '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="grid grid-cols-12 gap-6 py-8 border-b border-border last:border-0"
    >
      {/* Number */}
      <div className="col-span-3 sm:col-span-2">
        <div className="text-4xl sm:text-5xl font-display font-medium text-foreground">
          <AnimatedNumber value={numericValue} suffix={suffix} />
        </div>
      </div>

      {/* Label */}
      <div className="col-span-9 sm:col-span-3 flex items-center">
        <span className="text-base font-medium text-foreground">{label}</span>
      </div>

      {/* Description */}
      <div className="col-span-12 sm:col-span-7 flex items-center">
        <p className="text-muted-foreground">{description}</p>
      </div>
    </motion.div>
  );
}

export function Benefits() {
  const { t } = useI18n();

  const metrics = [
    t.benefits.items.time,
    t.benefits.items.insights,
    t.benefits.items.costs,
    t.benefits.items.accuracy,
  ];

  return (
    <section className="py-24 sm:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-12">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm font-medium text-primary mb-4"
          >
            {t.benefits.badge}
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl font-display font-medium text-foreground mb-4"
          >
            {t.benefits.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted-foreground"
          >
            {t.benefits.subtitle}
          </motion.p>
        </div>

        {/* Metrics table */}
        <div className="border-t border-border">
          {metrics.map((metric, index) => (
            <MetricRow
              key={index}
              value={metric.value}
              label={metric.label}
              description={metric.description}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
